import {
  Content,
  Item,
  ItemIndicator,
  ItemText,
  Portal,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import type { TFunction } from 'i18next';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { FIELD_VARIANT } from '@avanta/shared/constants/forms';
import { cn } from '@avanta/shared/utils/cn';
import { Text } from '../../Text';
import { FieldIcon } from '../FieldIcon';
import { Label } from '../Label';
import { dropdownTriggerEdit } from '../styles/dropdown';
import { SelectController } from './controller';
import {
  selectWrapper,
  selectTrigger,
  selectContent,
  selectViewport,
  selectItem,
  selectScrollButton,
  selectReadOnlyTrigger,
} from './styles';
import { type Option, type SelectProps } from './types';

const translateOptionLabel = (t: TFunction, option: { label: string; ns?: string }): string =>
  String(t(option.label, option.ns ? { ns: option.ns } : {}));

const resolveOptionElement = (
  slot?: React.JSX.Element,
  icon?: React.ElementType
): React.ReactNode | null => {
  if (!slot && !icon) return null;
  const IconComponent = icon as React.ElementType;
  if (slot) return slot;
  if (icon) return <IconComponent className="size-4 text-teal-600" />;
};

const Select = ({
  className,
  label,
  helperText,
  placeholder,
  options,
  size,
  variant = FIELD_VARIANT.FILLED,
  leftIcon: LeftIcon,
  leftSlot: LeftSlot,
  required,
  readOnly = false,
  ...props
}: SelectProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const value = props.value ?? '';
  const isHelperTextVisible = !!helperText?.trim();
  const selectedOption = options.find(option => String(option.value) === value);

  const leftElementNode = (): React.ReactNode | null => {
    const leftIcon = selectedOption?.leftIcon || LeftIcon;
    const leftSlot = selectedOption?.leftSlot || LeftSlot;

    if (leftSlot) return leftSlot;
    if (leftIcon) return <FieldIcon icon={leftIcon} />;
    return null;
  };

  const handleValueChange = (newValue: string): void => {
    if (readOnly) return;
    props.onValueChange?.(newValue);
  };

  const leftElement = leftElementNode();

  return (
    <div className={selectWrapper} translate="no" data-variant={variant}>
      {label && <Label required={required}>{label}</Label>}
      <Root
        data-slot="select"
        {...props}
        open={readOnly ? false : props.open}
        onOpenChange={readOnly ? undefined : props.onOpenChange}
        onValueChange={handleValueChange}
      >
        <Trigger
          data-slot="select-trigger"
          data-size={size || 'default'}
          data-has-value={readOnly ? undefined : !!value}
          data-has-left-icon={leftElement ? '' : undefined}
          data-readonly={readOnly}
          aria-readonly={readOnly}
          className={cn(
            selectTrigger,
            variant === FIELD_VARIANT.EDIT && !readOnly && dropdownTriggerEdit,
            readOnly && selectReadOnlyTrigger,
            className
          )}
        >
          {leftElement && (
            <div className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
              {leftElement}
            </div>
          )}
          <Value
            placeholder={placeholder}
            className="block min-w-0 max-w-full overflow-hidden text-left"
          >
            {value && (
              <Text
                tag="span"
                variant="text-sm-regular"
                className="block w-full truncate text-left"
              >
                {translateOptionLabel(t, selectedOption ?? { label: '' })}
              </Text>
            )}
          </Value>
          {readOnly ? null : (
            <ChevronDownIcon strokeWidth={1.25} className="size-6 text-denim-800" />
          )}
        </Trigger>
        <Portal>
          <Content
            position="popper"
            data-slot="select-content"
            className={cn(selectContent, 'notranslate')}
            translate="no"
          >
            <ScrollUpButton data-slot="select-scroll-up-button" className={selectScrollButton}>
              <ChevronUpIcon className="size-4" />
            </ScrollUpButton>
            <Viewport className={selectViewport}>
              {options.map(option => (
                <Item
                  key={option.value}
                  value={String(option.value)}
                  data-slot="select-item"
                  className={selectItem}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    {resolveOptionElement(option.leftSlot, option.leftIcon)}
                    <ItemText className="notranslate min-w-0 flex-1">
                      <Text
                        tag="span"
                        variant="text-sm-regular"
                        className={cn(
                          'flex min-w-0 flex-col',
                          selectedOption?.value === option.value && 'font-medium'
                        )}
                      >
                        <span className="line-clamp-2 wrap-break-word">
                          {translateOptionLabel(t, option)}
                        </span>
                        {option.description && (
                          <Text
                            tag="span"
                            variant="text-xs-regular"
                            className="truncate text-gray-500"
                          >
                            {option.description}
                          </Text>
                        )}
                      </Text>
                    </ItemText>
                  </div>
                  {String(option.value) !== value &&
                    resolveOptionElement(option.rightSlot, option.rightIcon)}
                  <ItemIndicator>
                    <CheckIcon className="size-4 text-teal-400" />
                  </ItemIndicator>
                </Item>
              ))}
            </Viewport>
            <ScrollDownButton data-slot="select-scroll-down-button" className={selectScrollButton}>
              <ChevronDownIcon className="size-4" />
            </ScrollDownButton>
          </Content>
        </Portal>
      </Root>
      {isHelperTextVisible && (
        <Text
          tag="span"
          variant="text-sm-light"
          className="group-data-[variant=invalid]/form:text-red-500"
        >
          {helperText}
        </Text>
      )}
    </div>
  );
};

export { SelectController, Select, type Option };
